<template>
  <div
    class="col-span-full row-start-3 w-full pt-2 px-[15px] overflow-hidden box-border relative"
  >
    <!-- Main tag row -->
    <div ref="container" class="flex items-center gap-2 w-auto max-w-full">
      <RanaTag
        v-for="(tag, idx) in visibleTags"
        :key="`${tag}-${idx}`"
        :tag="tag"
        draggable="true"
        @dragstart="ev => handleDragStart(ev, tag)"
        @dragend="handleDragEnd"
        @update:is-dragging="isDragging = $event"
        class="tag-item flex-shrink-0 min-w-fit py-1.5
               bg-[#021b27] border border-[#00ccaa]
               rounded-full text-sm whitespace-nowrap text-white"
      />

      <!-- More button -->
      <button
        v-if="clipped && !showOverlay"
        @click="toggleOverlay"
        class="tag-item flex-shrink-0 min-w-fit px-4 py-0.5
               bg-[#021b27] border border-[#00ccaa]
               rounded-full text-sm text-accent cursor-pointer"
      >
        More...
      </button>
    </div>

    <!-- Slide-up drawer -->
    <transition name="slide-up">
      <div
        v-if="showOverlay"
        class="fixed bottom-0 inset-x-0 bg-gradient-to-b from-[#061a25] to-[#05141d] 
              shadow-[0_-4px_20px_rgba(0,204,170,0.2)] border-t-2 border-[#00ccaa33]
              z-50 overflow-hidden p-4 max-h-64 rounded-t-lg"
      >
        <div class="absolute inset-0 overflow-hidden pointer-events-none">
          <div class="w-full h-[1px] bg-[#00ccaa50] absolute top-0 left-0 scanner-line"></div>
        </div>
        
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium">All Tags</h3>
          <button @click="toggleOverlay" class="text-sm text-accent underline cursor-pointer">
            Close
          </button>
        </div>
        <div class="flex flex-wrap gap-2">
          <RanaTag
            v-for="(tag, idx) in props.tags"
            :key="`${tag}-${idx}`"
            :tag="tag"
            draggable="true"
            @dragstart="ev => handleDragStart(ev, tag)"
            @dragend="handleDragEnd"
            @update:is-dragging="isDragging = $event"
           class="px-3 py-1 bg-gradient-to-r from-[#021b27] to-[#032537] 
                   border border-[#00ccaa] shadow-[0_0_8px_rgba(0,204,170,0.2)]
                   rounded-full text-sm whitespace-nowrap text-white
                   transition-all duration-200 hover:shadow-[0_0_12px_rgba(0,204,170,0.5)]
                   hover:border-[#00ffdd] hover:-translate-y-0.5"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick, computed } from 'vue';
import RanaTag from './RanaTag.vue';

const props = defineProps<{ tags: string[] }>();

// refs & state
const container = ref<HTMLElement|null>(null);
const visible = ref<boolean[]>(Array(props.tags.length).fill(true));
const showOverlay = ref(false);
const isDragging = ref(false);

// computed
const visibleTags = computed(() =>
  props.tags.filter((_, i) => visible.value[i])
);
const clipped = computed(() => visible.value.some(v => !v));

// measure fitting tags
function updateVisible() {
  if (!container.value) return;
  const maxWidth = container.value.clientWidth;
  const items = Array.from(container.value.querySelectorAll<HTMLElement>('.tag-item'));
  let total = 0;
  const newVis = items.map(item => {
    const style = getComputedStyle(item);
    const margin = parseFloat(style.marginRight) || 0;
    const w = item.offsetWidth + margin;
    if (total + w <= maxWidth) {
      total += w;
      return true;
    }
    return false;
  });
  // reserve slot for More...
  if (newVis.includes(false)) {
    const last = newVis.lastIndexOf(true);
    if (last >= 0) newVis[last] = false;
  }
  visible.value = newVis;
}

function toggleOverlay() {
  showOverlay.value = !showOverlay.value;
}

// drag handlers
function handleDragStart(ev: DragEvent, tag: string) {
  ev.dataTransfer?.setData('text', tag);
  ev.dataTransfer!.effectAllowed = 'move';
  isDragging.value = true;
}
function handleDragEnd(ev: DragEvent) {
  isDragging.value = false;
}

onMounted(() => {
  nextTick(updateVisible);
  window.addEventListener('resize', updateVisible);
});

watch(() => props.tags, () => {
  visible.value = Array(props.tags.length).fill(true);
  nextTick(updateVisible);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateVisible);
});
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
}
</style>
